import { useCallback, useContext, useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { PageSpinner } from '@ohpen/enso-ui';

import { optionsGetAllOptionsForFields, usersGetAll } from '@pmp/api';
import { getFieldsOfKind, PMP_APPLICATION_ID } from '@pmp/components';
import { buildFlagDescriptions, buildPermissionCatalogue, buildRoleCatalogue, PmpConfigContext, useHostChrome } from '@pmp/contexts';
import { useConfigAccess } from '@pmp/hooks';
import { buildCustomFields, buildParameters } from '@pmp/utils';
import { ConfigApp } from '@config';

/**
 * What PMP hands the configuration module. Mounted only on the configuration route, so the
 * catalogues are built when somebody opens it rather than on every page of the portal.
 */
export const ConfigHost = (): ReactElement => {
  const { t } = useTranslation();
  const { canViewSection } = useConfigAccess();
  const pmpConfig = useContext(PmpConfigContext);
  const {
    devClient: { selectedClient, clientSelect },
    aboutInfo,
    userAccount,
    portalName,
  } = useHostChrome();

  // Permissions are declared in this application's code, so the module is handed the catalogue.
  const permissionCatalogue = useMemo(() => buildPermissionCatalogue(t), [t]);
  const roleCatalogue = useMemo(() => buildRoleCatalogue(t, pmpConfig?.groupPermissions), [pmpConfig?.groupPermissions, t]);
  const flagDescriptions = useMemo(() => buildFlagDescriptions(t), [t]);
  const customFields = useMemo(() => buildCustomFields(t, pmpConfig?.additionalFields), [pmpConfig?.additionalFields, t]);
  const getParameters = useCallback(() => buildParameters(t, pmpConfig ?? undefined), [pmpConfig, t]);

  // The module reads every list through the host rather than owning a client of its own. Which
  // fields belong to each kind is PMP's knowledge, so the module only asks for one or the other.
  const getValueLists = useCallback(async (kind: 'fixed' | 'entity') => {
    const { data } = await optionsGetAllOptionsForFields({ query: { fields: getFieldsOfKind(kind) } });
    return data?.fields ?? {};
  }, []);

  const getUsers = useCallback(async () => {
    const { data } = await usersGetAll();
    return data ?? [];
  }, []);

  if (!portalName) return <PageSpinner />;

  return (
    <>
      {userAccount.modal}
      <ConfigApp
        application={PMP_APPLICATION_ID}
        applicationName={portalName}
        client={selectedClient}
        customMenu={clientSelect}
        onClickMyAccount={userAccount.open}
        aboutInfo={aboutInfo}
        configuration={pmpConfig}
        getValueLists={getValueLists}
        permissionCatalogue={permissionCatalogue}
        roleCatalogue={roleCatalogue}
        flagDescriptions={flagDescriptions}
        customFields={customFields}
        getParameters={getParameters}
        getUsers={getUsers}
        canViewSection={canViewSection}
      />
    </>
  );
};
